import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { apiClient } from '../services/api';
import { getCustomerId } from '../utils/authUser';
import { getMarketplaceEligibilityError } from '../utils/marketplaceEligibility';

const TERMINAL_INSTANCE_STATUSES = new Set(['terminated', 'failed']);

const isNonTerminalInstance = (instance) => {
  const status = (instance?.status || '').toLowerCase();
  return !TERMINAL_INSTANCE_STATUSES.has(status);
};

function Instances() {
  const { user, activeCustomerId } = useContext(AuthContext);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [marketplaceEligibilityError, setMarketplaceEligibilityError] = useState('');

  useEffect(() => {
    fetchInstances();
    const interval = setInterval(fetchInstances, 30000);
    return () => clearInterval(interval);
  }, [user, activeCustomerId]);

  const fetchInstances = async () => {
    try {
      const customerId = activeCustomerId || getCustomerId(user);
      if (!customerId) {
        setInstances([]);
        setMarketplaceEligibilityError('Customer ID not found. Please contact support.');
        return;
      }
      const [instancesResponse, customerResponse] = await Promise.all([
        apiClient.get(`/instances?customer_id=${customerId}`),
        apiClient.get(`/customers/${customerId}`).catch(() => ({ data: null }))
      ]);
      setInstances(instancesResponse.data.instances || []);
      setMarketplaceEligibilityError(getMarketplaceEligibilityError(customerResponse.data));
    } catch (error) {
      console.error('Error fetching instances:', error);
      setMarketplaceEligibilityError('Unable to validate AWS Marketplace agreement for this customer.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      running: 'badge-success',
      provisioning: 'badge-info',
      stopped: 'badge-warning',
      failed: 'badge-danger',
      terminated: 'badge-danger',
      terminating: 'badge-warning'
    };
    return statusClasses[status] || 'badge-info';
  };

  const getHealthBadge = (health) => {
    const healthClasses = {
      healthy: 'badge-success',
      unhealthy: 'badge-danger',
      unknown: 'badge-warning'
    };
    return healthClasses[health] || 'badge-warning';
  };

  const getHealthDisplay = (status, health) => {
    if (status === 'terminating' || status === 'terminated' || status === 'stopped') {
      return { label: 'N/A', badge: 'badge-info' };
    }

    const normalized = health || 'unknown';
    return { label: normalized, badge: getHealthBadge(normalized) };
  };

  const filteredInstances = instances.filter(instance => {
    if (filter === 'all') return true;
    return instance.status === filter;
  });
  const activeInstance = instances.find(isNonTerminalInstance) || null;
  const canCreateInstance = !activeInstance && !marketplaceEligibilityError;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div className="loading-spinner" style={{ borderTopColor: 'var(--primary-color)' }}></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Instances</h1>
        {canCreateInstance ? (
          <Link to="/instances/new" className="btn btn-primary">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Instance
          </Link>
        ) : (
          activeInstance ? (
            <Link to={`/instances/${activeInstance.instance_id}`} className="btn btn-secondary">
              View Current Instance
            </Link>
          ) : (
            <span className="btn btn-secondary" style={{ opacity: 0.65, cursor: 'not-allowed' }}>
              Marketplace Agreement Required
            </span>
          )
        )}
      </div>

      {!canCreateInstance && (
        activeInstance ? (
          <div className="card" style={{ marginBottom: '16px' }}>
            Single-instance limit active. Current instance:{' '}
            <Link to={`/instances/${activeInstance.instance_id}`} style={{ color: 'var(--primary-color)' }}>
              {activeInstance.project_name || activeInstance.instance_id}
            </Link>{' '}
            ({activeInstance.status}).
          </div>
        ) : marketplaceEligibilityError ? (
          <div className="card" style={{ marginBottom: '16px' }}>
            {marketplaceEligibilityError}
          </div>
        ) : null
      )}

      {/* Filters */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '8px' }}>
        {['all', 'running', 'provisioning', 'stopped', 'failed'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`btn ${filter === status ? 'btn-primary' : 'btn-secondary'}`}
            style={{ textTransform: 'capitalize' }}
          >
            {status}
          </button>
        ))}
      </div>

      {filteredInstances.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
            <h3>{filter === 'all' ? 'No instances yet' : `No ${filter} instances`}</h3>
            <p>
              {marketplaceEligibilityError
                ? marketplaceEligibilityError
                : filter === 'all'
                ? 'Create your first Virtuoso instance to get started'
                : `You don't have any instances with status "${filter}"`}
            </p>
            {filter === 'all' && canCreateInstance && (
              <Link to="/instances/new" className="btn btn-primary">Create Instance</Link>
            )}
          </div>
        </div>
      ) : (
        <div className="instance-grid">
          {filteredInstances.map(instance => {
            const healthDisplay = getHealthDisplay(instance.status, instance.health_status);

            return (
              <div key={instance.instance_id} className="instance-card">
                <div className="instance-card-header">
                  <h3>{instance.project_name || instance.instance_id}</h3>
                  <span className={`badge ${getStatusBadge(instance.status)}`}>
                    {instance.status}
                  </span>
                </div>

                <div className="instance-card-body">
                  <p><strong>Instance ID:</strong> {instance.instance_id}</p>
                  <p><strong>Tier:</strong> <span style={{ textTransform: 'capitalize' }}>{instance.tier}</span></p>
                  <p><strong>Region:</strong> {instance.region}</p>
                  <p>
                    <strong>Edition:</strong>{' '}
                    {instance.virtuoso_edition === 'commercial' ? (
                      <span className="badge" style={{ background: 'var(--primary-color)', color: 'white' }}>Commercial</span>
                    ) : (
                      <span className="badge" style={{ background: 'var(--success-color)', color: 'white' }}>Open Source</span>
                    )}
                  </p>
                  {instance.nlb_dns_name && (
                    <p><strong>Endpoint:</strong> {instance.nlb_dns_name.toLowerCase()}</p>
                  )}
                  <p>
                    <strong>Health:</strong>{' '}
                    <span className={`badge ${healthDisplay.badge}`}>
                      {healthDisplay.label}
                    </span>
                  </p>
                </div>

                <div className="instance-card-footer">
                  <Link to={`/instances/${instance.instance_id}`} className="btn btn-secondary">
                    View Details
                  </Link>
                  {instance.status === 'running' && instance.nlb_dns_name && (
                    <a
                      href={`https://${instance.nlb_dns_name.toLowerCase()}/conductor`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      Open Conductor
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Instances;
